import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, TrendingUp, ShieldCheck, Clock, Activity, 
  AlertTriangle, FileCheck, DollarSign, Search, Database, 
  FileText, Send, RefreshCw, BarChart3, Users, ArrowRight
} from 'lucide-react';
import { Button, Badge } from '@/ui';
import { useLocation } from 'wouter';

// Brand colors
const PRIMARY_BLUE = "#1356a6";
const ACCENT_COLOR = "#0AB89E";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function RevenueCycleManagement() {
  const [, setLocation] = useLocation();
  const heroLeftRef = useRef<HTMLDivElement | null>(null);
  const [heroMediaHeight, setHeroMediaHeight] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useLayoutEffect(() => {
    if (!heroLeftRef.current) return;

    const measure = () => {
      const el = heroLeftRef.current;
      if (!el) return;
      const next = Math.round(el.getBoundingClientRect().height);
      // Avoid thrashing state updates if the value didn't change.
      setHeroMediaHeight((prev) => (prev === next ? prev : next));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(heroLeftRef.current);
    window.addEventListener('resize', measure);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const handleBookDemo = () => {
    window.scrollTo(0, 0);
    setLocation('/book-demo');
  };

  const handleContact = () => {
    setLocation('/contact');
  };

  return (
    <div 
      className="min-h-screen font-sans overflow-x-hidden"
      style={{
        backgroundImage: "url(/assets/shared/simple-bg.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        fontFamily: "Montserrat, system-ui, sans-serif"
      }}
    >
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 overflow-hidden">
        <div className="container mx-auto px-4 grid lg:grid-cols-2 gap-12 items-start">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-2xl relative z-10"
          >
            <div ref={heroLeftRef}>
              <motion.div variants={fadeInUp}>
                <Badge className="mb-6 border-none" style={{ backgroundColor: `${PRIMARY_BLUE}20`, color: PRIMARY_BLUE }}>
                  <span className="relative flex h-2 w-2 mr-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: PRIMARY_BLUE }}></span>
                    <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: PRIMARY_BLUE }}></span>
                  </span>
                  New: AI Copilot for Denials
                </Badge>
              </motion.div>

              <motion.h1 
                variants={fadeInUp}
                className="text-4xl lg:text-5xl font-normal leading-[1.1] mb-6"
                style={{ color: PRIMARY_BLUE }}
              >
                Maximize Every Dollar of{' '}
                <span 
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, ${ACCENT_COLOR} 100%)`
                  }}
                >
                  Earned Revenue
                </span>
              </motion.h1>

              <motion.p variants={fadeInUp} className="text-xl text-gray-600 mb-8 leading-relaxed max-w-lg">
                From eligibility verification to final payment, automate your entire revenue cycle with AI that predicts denials and accelerates collections.
              </motion.p>
              
              <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto font-bold gap-2 rounded-full transition-all duration-200 hover:-translate-y-1 active:translate-y-0"
                  style={{
                    backgroundColor: PRIMARY_BLUE,
                    color: "white",
                    boxShadow: "0 4px 0 #0a2d4d, 0 6px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.2)",
                  }}
                  onClick={handleBookDemo}
                >
                  Request a Demo
                  <ArrowRight className="w-5 h-5" style={{ color: ACCENT_COLOR }} />
                </Button>
              </motion.div>

              <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-4 mt-12">
                {[
                  { icon: CheckCircle, value: "98%", label: "First-Pass Clean Claims" },
                  { icon: TrendingUp, value: "+35%", label: "Faster Collections" },
                  { icon: ShieldCheck, value: "-50%", label: "Denial Write-Offs" },
                  { icon: Clock, value: "-2 days", label: "Days in AR Reduction" }
                ].map((stat, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-white/80 backdrop-blur-sm p-3 rounded-xl border border-white/50 shadow-sm">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: `${ACCENT_COLOR}20`, color: ACCENT_COLOR }}>
                      <stat.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-normal" style={{ color: PRIMARY_BLUE }}>{stat.value}</div>
                      <div className="text-xs text-gray-500 font-normal">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative hidden lg:flex items-start"
            style={heroMediaHeight ? { height: heroMediaHeight } : undefined}
          >
            <div className="relative w-full h-full min-h-0">
              <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/50 bg-white/5 backdrop-blur-sm p-2 h-full">
                <img 
                  src="/assets/solutions/revenue-cycle/3d_healthcare_revenue_cycle_management_dashboard_hero.png"
                  alt="RCM Dashboard Interface" 
                  className="rounded-xl w-full h-full object-cover object-center" 
                />
              </div>
              
              
              {/* Floating Collections Badge */}
              <motion.div 
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                className="absolute top-6 left-6 bg-white p-4 rounded-xl shadow-xl border border-gray-100 max-w-[220px] z-20"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                    <DollarSign className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Collections Today</div>
                    <div className="font-normal" style={{ color: PRIMARY_BLUE }}>$142,590</div>
                  </div>
                </div>
                <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 w-[85%]"></div>
                </div>
              </motion.div>

              {/* Floating AI Alert */}
              <motion.div 
                animate={{ y: [0, 10, 0] }}
                transition={{ repeat: Infinity, duration: 5, ease: "easeInOut", delay: 1 }}
                className="absolute bottom-8 right-6 bg-white p-4 rounded-xl shadow-xl border border-gray-100 z-20 max-w-[240px]"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: `${PRIMARY_BLUE}20`, color: PRIMARY_BLUE }}>
                    <Activity className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-normal" style={{ color: PRIMARY_BLUE }}>AI Copilot Alert</div>
                    <div className="text-xs text-gray-500">Denial risk detected (Code 271)</div>
                  </div>
                </div>
              </motion.div>

              {/* Floating Clean Claims Rate */}
              <motion.div 
                animate={{ y: [0, -8, 0] }}
                transition={{ repeat: Infinity, duration: 4.5, ease: "easeInOut", delay: 0.5 }}
                className="absolute top-28 left-10 bg-white p-4 rounded-xl shadow-xl border border-gray-100 z-20"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: `${ACCENT_COLOR}20`, color: ACCENT_COLOR }}>
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Clean Claims Rate</div>
                    <div className="font-normal" style={{ color: PRIMARY_BLUE }}>98.5%</div>
                  </div>
                </div>
              </motion.div>

              {/* Floating AR Days */}
              <motion.div 
                animate={{ x: [0, 8, 0] }}
                transition={{ repeat: Infinity, duration: 5.5, ease: "easeInOut", delay: 2 }}
                className="absolute top-20 right-10 bg-white p-4 rounded-xl shadow-xl border border-gray-100 z-20"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Avg AR Days</div>
                    <div className="font-normal" style={{ color: PRIMARY_BLUE }}>26</div>
                  </div>
                </div>
              </motion.div>

              {/* Floating Denial Prevention */}
              <motion.div 
                animate={{ y: [0, 8, 0] }}
                transition={{ repeat: Infinity, duration: 6, ease: "easeInOut", delay: 1.5 }}
                className="absolute bottom-24 left-12 bg-white p-4 rounded-xl shadow-xl border border-gray-100 z-20"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100 text-purple-600">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Denied Claims</div>
                    <div className="font-normal" style={{ color: PRIMARY_BLUE }}>2.1%</div>
                  </div>
                </div>
              </motion.div>

              {/* Floating Auto-Posting */}
              <motion.div 
                animate={{ x: [0, -6, 0], y: [0, 6, 0] }}
                transition={{ repeat: Infinity, duration: 5, ease: "easeInOut", delay: 2.5 }}
                className="absolute bottom-10 left-1/2 -translate-x-1/2 bg-white p-3 rounded-xl shadow-xl border border-gray-100 z-20"
              >
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                    <RefreshCw className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Auto-Posted</div>
                    <div className="font-normal text-sm" style={{ color: PRIMARY_BLUE }}>$89K</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Dashboard Section */}
      <section className="py-20 bg-slate-50/50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-4" style={{ color: PRIMARY_BLUE }}>
              Real-Time Revenue Intelligence
            </h2>
            <p className="text-lg text-gray-600">
              Monitor claims, collections, denials, and cash flow in real-time. AI-powered insights surface issues before they impact your bottom line.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { label: "First Pass Rate", value: "97.2%", target: "> 95%", trend: "up" as const },
              { label: "Net Collection Rate", value: "96.8%", target: "> 95%", trend: "up" as const },
              { label: "Denial Rate", value: "3.8%", target: "< 5%", trend: "up" as const },
              { label: "Days in AR", value: "28", target: "< 30", trend: "up" as const }
            ].map((metric, idx) => (
              <div key={idx} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                <div className="text-sm text-gray-500 mb-1">{metric.label}</div>
                <div className="flex items-end justify-between">
                  <div className="text-2xl font-normal" style={{ color: PRIMARY_BLUE }}>{metric.value}</div>
                  <div className="text-xs font-normal px-2 py-1 rounded-full flex items-center gap-1 bg-green-100 text-green-700">
                    <TrendingUp className="w-3 h-3" />
                    Improving
                  </div>
                </div>
                <div className="text-xs text-gray-400 mt-2">Target: {metric.target}</div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: FileCheck, title: "Claims Ready Counter", desc: "Real-time count of claims ready to submit with quality score.", benefit: "Know your pipeline" },
              { icon: AlertTriangle, title: "Rejection Monitor", desc: "X12 999/277CA rejection tracking with trending analysis.", benefit: "Catch errors early" },
              { icon: DollarSign, title: "Cash Today Widget", desc: "Real-time collections with source breakdown.", benefit: "Daily visibility" },
              { icon: Activity, title: "AI Copilot Insights", desc: "Proactive alerts for denial spikes, AR aging, and trends.", benefit: "Intelligent ops" }
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-colors mb-4" style={{ backgroundColor: ACCENT_COLOR }}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-normal mb-2" style={{ color: PRIMARY_BLUE }}>{feature.title}</h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">{feature.desc}</p>
                <div className="pt-4 border-t border-gray-100">
                  <span className="text-xs font-normal uppercase tracking-wider" style={{ color: PRIMARY_BLUE }}>Benefit</span>
                  <p className="text-sm font-normal text-gray-800 mt-1">{feature.benefit}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-sm">
            <div className="grid lg:grid-cols-2 gap-16 items-stretch">
              <div className="order-2 lg:order-1 flex items-center justify-center">
                <div className="overflow-hidden rounded-2xl shadow-lg" style={{ width: '120%' }}>
                  <img 
                    src="/assets/solutions/revenue-cycle/3d_abstract_medical_verification_symbol.png"
                    alt="Eligibility Verification" 
                    className="h-[320px] sm:h-[380px] lg:h-[420px] w-full object-cover"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2 flex flex-col justify-center">
                <Badge className="mb-6 border-none" style={{ backgroundColor: `${ACCENT_COLOR}20`, color: ACCENT_COLOR }}>
                  270/271 Integration
                </Badge>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-6" style={{ color: PRIMARY_BLUE }}>
                  Verify Before You Bill
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Verify patient eligibility and benefits in real-time—before the visit, at check-in, or any time. Reduce claim denials caused by eligibility issues by 90%.
                </p>
                
                <div className="space-y-4">
                  {[
                    "Real-Time 271 Response with parsed details",
                    "Batch Eligibility for upcoming appointments",
                    "Coverage Dashboard for deductibles & OOP",
                    "Multi-Payer Support (Medicare, Medicaid, Commercial)"
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="mt-1 p-1 bg-green-100 rounded-full text-green-600">
                        <CheckCircle className="w-4 h-4" />
                      </div>
                      <span className="text-gray-700 font-normal">{item}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-8 pt-8 border-t border-gray-200 flex gap-8">
                  <div>
                    <div className="text-3xl font-normal" style={{ color: PRIMARY_BLUE }}>450+</div>
                    <div className="text-sm text-gray-500">Verifications/Day</div>
                  </div>
                  <div>
                    <div className="text-3xl font-normal" style={{ color: PRIMARY_BLUE }}>99.2%</div>
                    <div className="text-sm text-gray-500">Batch Success Rate</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Charge Capture Section */}
      <section 
        className="py-24 text-white relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, #0f4b91 50%, #0a3568 100%)`
        }}
      >
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        
        <div className="container mx-auto px-4 grid lg:grid-cols-2 gap-16 items-center relative z-10">
          <div>
            <Badge className="mb-6 border-none" style={{ backgroundColor: `${ACCENT_COLOR}`, color: "white" }}>
              AI-Assisted Coding
            </Badge>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal text-white mb-6">
              Capture Every Charge, Code It Right
            </h2>
            <p className="text-lg text-slate-300 mb-8 leading-relaxed">
              Never miss a billable service. AI suggests codes from clinical documentation, validates medical necessity, and ensures compliant charge capture.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                { icon: Search, title: "Encounter Browser", desc: "Filter and search encounters by status, provider, date." },
                { icon: Database, title: "AI Code Suggestions", desc: "Auto-suggest CPT/ICD-10 from documentation." },
                { icon: ShieldCheck, title: "Medical Necessity", desc: "Real-time LCD/NCD validation checks." },
                { icon: FileText, title: "Modifier Assistant", desc: "AI-suggested modifiers based on rules." }
              ].map((feature, idx) => (
                <div key={idx} className="rounded-xl backdrop-blur-md border border-white/20 shadow-lg p-4 bg-white/10">
                  <feature.icon className="w-6 h-6 mb-3" style={{ color: ACCENT_COLOR }} />
                  <h4 className="font-normal text-lg mb-1">{feature.title}</h4>
                  <p className="text-sm text-slate-400">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative flex items-center justify-center">
            <div className="absolute inset-0 blur-3xl rounded-full" style={{ backgroundColor: `${PRIMARY_BLUE}40` }}></div>
            <div className="overflow-hidden rounded-2xl shadow-2xl border border-white/10" style={{ width: '120%' }}>
              <img 
                src="/assets/solutions/revenue-cycle/3d_smart_medical_coding_tablet.png"
                alt="AI Coding Assistant" 
                className="relative h-[320px] sm:h-[380px] lg:h-[420px] w-full object-cover" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Clean Claims Section */}
      <section className="py-24 bg-blue-50/50">
        <div className="container mx-auto px-4 text-center mb-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-4" style={{ color: PRIMARY_BLUE }}>
            Clean Claims, First Time
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Catch and fix claim errors before submission. Our AI-powered scrubber validates against payer rules, CCI edits, and billing best practices.
          </p>
        </div>
        
        <div className="container mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
          <div className="flex items-center justify-center">
            <div className="overflow-hidden rounded-2xl shadow-xl" style={{ width: '120%' }}>
              <img 
                src="/assets/solutions/revenue-cycle/3d_clean_claim_processing_concept.png"
                alt="Clean Claims" 
                className="h-[320px] sm:h-[380px] lg:h-[420px] w-full object-cover" 
              />
            </div>
          </div>
          <div className="space-y-6">
            {[
              { icon: RefreshCw, color: "blue", title: "Multi-Layer Validation", desc: "CCI, LCD, NCD, Payer Rules, Modifier, and Billing edits checked simultaneously." },
              { icon: CheckCircle, color: "purple", title: "One-Click Fixes", desc: "AI-suggested corrections with a single apply button for faster resolution." },
              { icon: BarChart3, color: "orange", title: "Error Analytics", desc: "Track common error patterns by type, payer, and provider to improve processes." }
            ].map((feature, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-start gap-4 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className={`p-3 rounded-lg bg-${feature.color}-100 text-${feature.color}-600`}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-xl font-normal mb-2" style={{ color: PRIMARY_BLUE }}>{feature.title}</h4>
                  <p className="text-gray-600">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Complete Revenue Cycle Control */}
      <section className="py-24 bg-blue-50/50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-4" style={{ color: PRIMARY_BLUE }}>
              Complete Revenue Cycle Control
            </h2>
            <p className="text-lg text-gray-600">
              Fifteen interconnected modules working together.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: FileText, title: "Claim Builder", desc: "Generate CMS-1500 and UB-04 claims with all required data elements.", items: ["Professional & Institutional", "Attachment Manager (PWK)", "Payer-Specific Fields"] },
              { icon: Send, title: "Clearinghouse Integration", desc: "End-to-end claim transmission and tracking.", items: ["Real-time Status Updates", "999/277CA/835 Handling", "Error Parser"] },
              { icon: Database, title: "ERA Auto-Posting", desc: "Lights-out remittance processing from 835 files.", items: ["Auto-Post Rules", "Recoupment Management", "Underpayment Detection"] },
              { icon: AlertTriangle, title: "Denial Management", desc: "Identify root causes and automate appeals.", items: ["AI Appeal Builder", "Root Cause Analysis", "Prioritized Worklists"] },
              { icon: Users, title: "Smart AR Worklists", desc: "Focus your team on accounts that matter most.", items: ["AI Priority Scoring", "Payer Call Scripts", "Automated Follow-up"] },
              { icon: DollarSign, title: "Patient Billing", desc: "Modern patient financial experience.", items: ["Real-Time Estimates", "Payment Plans", "Text-to-Pay"] }
            ].map((module, idx) => (
              <div 
                key={idx} 
                className="group rounded-2xl p-6 border border-white/30 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 hover:border-white/50"
                style={{ 
                  background: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, #0f4b91 100%)`,
                }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 shadow-lg" style={{ backgroundColor: ACCENT_COLOR }}>
                  <module.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-normal mb-3 text-white">{module.title}</h3>
                <p className="text-slate-300 text-sm mb-4">{module.desc}</p>
                <ul className="text-sm space-y-2 text-slate-400">
                  {module.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ACCENT_COLOR }}></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-24 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, #0f4b91 50%, #0a3568 100%)`
        }}
      >
        <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-900/40 via-transparent to-transparent"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal text-white mb-6">
            Ready to optimize your revenue cycle?
          </h2>
          <p className="text-xl text-slate-100 mb-10">
            Join forward-thinking urgent care providers who are reducing denials and accelerating cash flow with Aiscendus.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              size="lg" 
              className="w-full sm:w-auto font-bold gap-2 rounded-full transition-all duration-200 hover:-translate-y-1 active:translate-y-0"
              style={{
                backgroundColor: "white",
                color: PRIMARY_BLUE,
                boxShadow: "0 4px 0 rgba(255,255,255,0.8), 0 6px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.2)",
              }}
              onClick={handleBookDemo}
            >
              Request a Personalized Demo
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
